import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  User, Menu, LogOut, Lock,
  LayoutDashboard, Building2, Stethoscope, UserCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Logo } from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export const Navbar = () => {
  const { isAuthenticated, user, profile, logout, isPatient, isProvider, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isProvider) return '/prestador';
    if (isPatient) return '/paciente';
    return '/';
  };

  const getDashboardLabel = () => {
    if (isAdmin) return 'Painel Admin';
    if (isProvider) return 'Painel Clínica';
    if (isPatient) return 'Minha Área';
    return 'Dashboard';
  };

  const getProfileLink = () => {
    if (isProvider) return '/prestador/perfil';
    if (isPatient) return '/paciente/perfil';
    if (isAdmin) return '/admin';
    return getDashboardLink();
  };

  return (
    <header className="bg-white/95 backdrop-blur-xl sticky top-0 z-50 border-b border-border/40" data-testid="navbar">
      <div className="container-app">
        <div className="flex items-center justify-between h-[72px] md:h-20">
          {/* Left cluster: Logo + Nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="inline-flex items-center" data-testid="logo-link">
              <Logo size="md" className="scale-[1.06] origin-left" />
            </Link>

            {/* Desktop Navigation - Hide for providers and admins */}
            {!isProvider && !isAdmin && (
              <nav className="hidden md:flex items-center gap-1" aria-label="Navegação principal">
                <Link
                  to="/ecosistema"
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-primary hover:bg-primary/5 rounded-full transition-colors"
                  data-testid="nav-ecosystem-link"
                >
                  Ecossistema Saúde
                </Link>
                <Link
                  to="/como-funciona-pj"
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-primary hover:bg-primary/5 rounded-full transition-colors"
                  data-testid="nav-pj-link"
                >
                  Para Clínicas e Profissionais
                </Link>
                <Link
                  to="/sobre"
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-primary hover:bg-primary/5 rounded-full transition-colors"
                  data-testid="nav-about-link"
                >
                  Sobre
                </Link>
              </nav>
            )}
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-primary/5" data-testid="user-menu-trigger">
                    <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden md:block text-sm font-medium text-slate-700 max-w-[150px] truncate">
                      {profile?.full_name || profile?.trade_name || user?.email}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {/*
                    <DropdownMenuItem asChild>
                      <Link to="/prestador/medicos" className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Gerenciar Médicos
                      </Link>
                    </DropdownMenuItem>
                  */}
                  <DropdownMenuItem asChild>
                    <Link to={getProfileLink()} className="flex items-center gap-2" data-testid="my-profile-link">
                      <UserCircle className="w-4 h-4" />
                      Meu perfil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/conta/senha" className="flex items-center gap-2" data-testid="change-password-link">
                      <Lock className="w-4 h-4" />
                      Alterar Senha
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button className="bg-accent text-accent-foreground hover:bg-[#E89715] rounded-full px-5 font-semibold shadow-[0_4px_14px_rgba(245,166,35,0.22)]" data-testid="login-button">
                    Entrar
                  </Button>
                </Link>
                <Link to="/cadastro" className="hidden md:block">
                  <Button variant="outline" className="rounded-full border-primary/25 bg-white px-5 text-primary hover:bg-primary/5" data-testid="register-button">
                    Sou profissional
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <nav className="flex flex-col gap-4 mt-8">
                  {/* Public links (visible to all non-provider/admin) */}
                  {!isProvider && !isAdmin && (
                    <>
                      <Link
                        to="/ecosistema"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-nav-ecosystem-link"
                      >
                        <Stethoscope className="w-5 h-5 text-primary" />
                        <span className="font-medium">Ecossistema Saúde</span>
                      </Link>
                      <Link
                        to="/como-funciona-pj"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-nav-pj-link"
                      >
                        <Building2 className="w-5 h-5 text-primary" />
                        <span className="font-medium">Para Clínicas e Profissionais</span>
                      </Link>
                      <Link
                        to="/sobre"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-nav-about-link"
                      >
                        <User className="w-5 h-5 text-primary" />
                        <span className="font-medium">Sobre</span>
                      </Link>
                    </>
                  )}
                  {/* Provider/Admin quick links */}
                  {(isProvider || isAdmin) && (
                    <Link 
                      to={getDashboardLink()} 
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-5 h-5 text-primary" />
                      <span className="font-medium">{getDashboardLabel()}</span>
                    </Link>
                  )}
                  {!isAuthenticated && (
                    <>
                      <div className="border-t my-2" />
                      <Link 
                        to="/login" 
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <User className="w-5 h-5 text-primary" />
                        <span className="font-medium">Entrar</span>
                      </Link>
                      <Link 
                        to="/cadastro" 
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button variant="outline" className="w-full rounded-full border-primary/25 text-primary hover:bg-primary/5">Sou profissional</Button>
                      </Link>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
